/**
 * Authentication Context
 * Manages user authentication state and provides auth methods throughout the app
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';

// User role types based on Django RBAC
export type UserRole = 'student' | 'teacher' | 'admin' | 'school_admin';

// Teacher profile interface
interface TeacherProfile {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  employee_id: string;
  department: string;
  hire_date: string;
  is_school_admin: boolean;
  is_consultant?: boolean;
  school?: {
    id: number;
    name: string;
  };
}

// Learner profile interface
interface LearnerProfile {
  id: number;
  student_id: string;
  grade_level: number;
  school: number;
  classes: number[];
}

// User profile interface
interface UserProfile {
  role: UserRole;
  teacher_profile?: TeacherProfile;
  learner_profile?: LearnerProfile;
}

// Favorite navigation item interface
export interface FavoriteNavigationItem {
  id: number;
  item_id: string;
  name: string;
  route: string;
  icon: string;
  section: string;
  category: string;
  order: number;
  created_at: string;
}

// Pinned navigation item interface
export interface PinnedNavigationItem {
  id: number;
  item_id: string;
  name: string;
  route: string;
  icon: string;
  section: string;
  category: string;
  order: number;
  created_at: string;
}

// User interface
export interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  is_superuser: boolean;
  is_staff: boolean;
  user_profile: UserProfile;
  favorites?: FavoriteNavigationItem[];
  pinned_items?: PinnedNavigationItem[];
}

// Authentication result interface
export interface AuthResult {
  success: boolean;
  auth_method: string;
  adam_status: string;
  debug?: {
    adam_attempted: boolean;
    adam_success: boolean;
    local_attempted: boolean;
    local_success: boolean;
    fallback_reason?: string;
  };
}

// Auth context interface
interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  authReady: boolean; // NEW: Signals that auth system is fully initialized
  login: (username: string, password: string) => Promise<AuthResult>;
  logout: () => void;
  isAuthenticated: boolean;
  hasRole: (role: UserRole | UserRole[]) => boolean;
  isTeacher: boolean;
  isStudent: boolean;
  isAdmin: boolean;
  isSchoolAdmin: boolean;
  isConsultant: boolean;
  impersonatedLearnerGrade: number | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('authToken'));
  const [loading, setLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false); // NEW: Track auth initialization
  const [isImpersonating, setIsImpersonating] = useState(false); // Track impersonation state
  const [impersonatedLearnerGrade, setImpersonatedLearnerGrade] = useState<number | null>(null);

  // Load user data on mount or when token changes
  useEffect(() => {
    const loadUser = async () => {
      const storedToken = localStorage.getItem('authToken');

      if (storedToken) {
        setToken(storedToken);
        // NOTE: Don't set axios.defaults - the interceptor in axiosConfig.ts handles this

        try {
          // Fetch current user data from Django
          const response = await axios.get('/api/me/');
          console.log('[AuthContext] /api/me/ response:', response.data);
          console.log('[AuthContext] pinned_items in response:', response.data.pinned_items);
          setUser(response.data);

          // Check for active impersonation session
          const impersonationToken = localStorage.getItem('impersonationToken');
          if (impersonationToken) {
            try {
              const impResponse = await axios.get('/api/impersonate/status/');
              if (impResponse.data.is_impersonating) {
                setIsImpersonating(true);
                setImpersonatedLearnerGrade(impResponse.data.learner?.grade_level ?? null);
                console.log('[AuthContext] Active impersonation session detected');
              } else {
                setIsImpersonating(false);
                setImpersonatedLearnerGrade(null);
                // Token is invalid, remove it
                localStorage.removeItem('impersonationToken');
              }
            } catch (error) {
              console.error('Failed to check impersonation status:', error);
              setIsImpersonating(false);
              localStorage.removeItem('impersonationToken');
            }
          } else {
            setIsImpersonating(false);
          }
        } catch (error) {
          console.error('Failed to load user:', error);
          // Token might be invalid, clear it
          localStorage.removeItem('authToken');
          localStorage.removeItem('username');
          setToken(null);
          setUser(null);
          // NOTE: Don't need to delete axios.defaults - interceptor reads from localStorage
        }
      }

      setLoading(false);
      // IMPORTANT: Set authReady AFTER all auth operations complete
      // This ensures components don't make API calls until auth is configured
      setAuthReady(true);
    };

    loadUser();
  }, []);

  // Listen for impersonation state changes
  useEffect(() => {
    const checkImpersonation = async () => {
      const impersonationToken = localStorage.getItem('impersonationToken');
      if (impersonationToken && user) {
        try {
          const response = await axios.get('/api/impersonate/status/');
          setIsImpersonating(response.data.is_impersonating);
          setImpersonatedLearnerGrade(response.data.is_impersonating ? (response.data.learner?.grade_level ?? null) : null);
        } catch (error) {
          console.error('Failed to check impersonation status:', error);
          setIsImpersonating(false);
          setImpersonatedLearnerGrade(null);
          localStorage.removeItem('impersonationToken');
        }
      } else {
        setIsImpersonating(false);
      }
    };

    // Check impersonation status on mount and periodically
    checkImpersonation();
    const interval = setInterval(checkImpersonation, 5000); // Check every 5 seconds

    return () => clearInterval(interval);
  }, [user]);

  const login = async (username: string, password: string) => {
    try {
      const response = await axios.post('/api/adam-auth/login/', {
        username,
        password,
      });

      if (response.data.token) {
        const newToken = response.data.token;
        const userData = response.data.user;

        // Log authentication method and debug info
        console.log('🔐 Authentication successful!');
        console.log('   Method:', response.data.auth_method || 'unknown');
        console.log('   ADAM Status:', response.data.adam_status || 'unknown');

        if (response.data.debug) {
          console.log('   Debug Info:', {
            adam_attempted: response.data.debug.adam_attempted,
            adam_success: response.data.debug.adam_success,
            local_attempted: response.data.debug.local_attempted,
            local_success: response.data.debug.local_success,
            fallback_reason: response.data.debug.fallback_reason
          });
        }

        // Store token in localStorage - interceptor will pick it up automatically
        localStorage.setItem('authToken', newToken);
        localStorage.setItem('username', username);
        setToken(newToken);
        setUser(userData);

        // NOTE: Don't set axios.defaults - the interceptor in axiosConfig.ts handles this
        // This eliminates race conditions and keeps auth header management in one place

        // Mark auth as ready after login completes
        setAuthReady(true);

        // Return auth info for the LoginPage to display
        return {
          success: true,
          auth_method: response.data.auth_method,
          adam_status: response.data.adam_status,
          debug: response.data.debug
        };
      } else {
        throw new Error('No token received from server');
      }
    } catch (error: any) {
      console.error('❌ Login error:', error);

      // Log failed authentication details
      if (error.response?.data) {
        console.log('   Auth Method:', error.response.data.auth_method || 'none');
        console.log('   ADAM Status:', error.response.data.adam_status || 'unknown');
        console.log('   Fallback Reason:', error.response.data.fallback_reason || 'N/A');
      }

      throw error;
    }
  };

  const logout = () => {
    // Clear local storage - this is enough, interceptor will stop adding headers
    localStorage.removeItem('authToken');
    localStorage.removeItem('username');
    localStorage.removeItem('impersonationToken');

    // Clear state
    setToken(null);
    setUser(null);
    setIsImpersonating(false);
    setAuthReady(false); // Reset auth ready on logout

    // NOTE: Don't need to delete axios.defaults - interceptor reads from localStorage
  };

  const hasRole = (roles: UserRole | UserRole[]): boolean => {
    if (!user || !user.user_profile) return false;

    // When impersonating a learner, the effective role is 'student'
    const effectiveRole = isImpersonating ? 'student' : user.user_profile.role;
    const roleArray = Array.isArray(roles) ? roles : [roles];

    return roleArray.includes(effectiveRole);
  };

  const isAuthenticated = !!user && !!token;
  // When impersonating, override role checks to treat as student
  const isTeacher = isImpersonating ? false : hasRole('teacher');
  const isStudent = isImpersonating ? true : hasRole('student');
  const isAdmin = isImpersonating ? false : (hasRole('admin') || user?.is_superuser || user?.is_staff || false);
  const isSchoolAdmin = isImpersonating ? false : (user?.user_profile?.teacher_profile?.is_school_admin || false);
  const isConsultant = isImpersonating ? false : (user?.user_profile?.teacher_profile?.is_consultant || false);

  const value: AuthContextType = {
    user,
    token,
    loading,
    authReady, // NEW: Export authReady flag
    login,
    logout,
    isAuthenticated,
    hasRole,
    isTeacher,
    isStudent,
    isAdmin,
    isSchoolAdmin,
    isConsultant,
    impersonatedLearnerGrade,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
